import React, { useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import {
  Brain,
  Download,
  Loader2,
  RefreshCw,
  Sparkles,
  FileText,
  TrendingUp,
  Users,
  AlertTriangle,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  useLlmStatus,
  useMultiAgentReport,
  type OptimizationRunDetail,
} from '@/hooks/optimization/useOptimizationRuns';

interface MultiAgentReportProps {
  run: OptimizationRunDetail;
  /** Когда true — показывать индикатор «генерируется», даже если report пустой */
  waitingForGeneration?: boolean;
}

const TAB_DEFS = [
  {
    key: 'summary' as const,
    label: 'Резюме',
    icon: FileText,
    field: 'summary' as const,
    accent: 'indigo',
    placeholder:
      'Итоговый отчёт бизнес-консультанта появится здесь после генерации.',
  },
  {
    key: 'sales' as const,
    label: 'Продажи',
    icon: TrendingUp,
    field: 'sales' as const,
    accent: 'emerald',
    placeholder: 'Анализ продаж, пиков и аномалий.',
  },
  {
    key: 'schedule' as const,
    label: 'График',
    icon: Users,
    field: 'schedule' as const,
    accent: 'blue',
    placeholder:
      'Объяснение работы солвера, ФОТ, покрытия и распределения между сотрудниками.',
  },
  {
    key: 'risks' as const,
    label: 'Риски',
    icon: AlertTriangle,
    field: 'risks' as const,
    accent: 'amber',
    placeholder: 'Операционные риски и рекомендации по митигации.',
  },
];

export const MultiAgentReport: React.FC<MultiAgentReportProps> = ({
  run,
  waitingForGeneration,
}) => {
  const { data: llm } = useLlmStatus();
  const mutate = useMultiAgentReport();

  const report = run.llm_agents_output ?? null;
  const hasReport = !!report?.summary && report.summary.trim().length > 0;

  const handleRegenerate = () =>
    mutate.mutate({ runId: run.id, force: true });
  const handleGenerate = () =>
    mutate.mutate({ runId: run.id, force: false });

  const handleDownloadPdf = useCallback(async () => {
    if (!report) return;
    const html2pdf = (await import('html2pdf.js')).default;

    const sections = [
      { title: 'Анализ продаж', content: report.sales },
      { title: 'Анализ графика', content: report.schedule },
      { title: 'Анализ рисков', content: report.risks },
      { title: 'Резюме', content: report.summary },
    ];

    const period = `${run.period_start} — ${run.period_end}`;
    const model = report.model?.replace(/-\d{8}$/, '') ?? '—';
    const date = report.generated_at
      ? new Date(report.generated_at).toLocaleString('ru-RU')
      : '—';

    const pagesHtml = sections
      .map(
        (s) =>
          `<div class="page"><h1>${s.title}</h1>${marked.parse(s.content?.trim() || '*Нет данных*')}</div>`,
      )
      .join('');

    const container = document.createElement('div');
    container.innerHTML = `
      <style>
        .page { page-break-after: always; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1e293b; padding: 0; }
        .page:last-child { page-break-after: auto; }
        .header-meta { font-size: 11px; color: #64748b; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-bottom: 16px; }
        h1 { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 16px 0; padding-bottom: 8px; border-bottom: 2px solid #6366f1; }
        h2 { font-size: 16px; font-weight: 600; color: #1e293b; margin: 18px 0 8px 0; }
        h3 { font-size: 14px; font-weight: 600; color: #334155; margin: 14px 0 6px 0; }
        p { font-size: 13px; line-height: 1.6; margin: 6px 0; }
        ul, ol { font-size: 13px; padding-left: 20px; margin: 6px 0; }
        li { margin: 3px 0; line-height: 1.5; }
        strong { font-weight: 600; color: #0f172a; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; margin: 10px 0; }
        th { background: #f1f5f9; font-weight: 600; padding: 6px 8px; border: 1px solid #e2e8f0; text-align: left; }
        td { padding: 6px 8px; border: 1px solid #e2e8f0; }
        blockquote { border-left: 3px solid #6366f1; background: #eef2ff; padding: 6px 12px; margin: 8px 0; font-size: 13px; }
        code { background: #eef2ff; color: #4338ca; padding: 1px 4px; border-radius: 3px; font-size: 12px; }
      </style>
      <div class="header-meta">Период: ${period} &nbsp;|&nbsp; Модель: ${model} &nbsp;|&nbsp; ${date}</div>
      ${pagesHtml}
    `;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (html2pdf() as any)
      .set({
        margin: [10, 12, 10, 12],
        filename: `AI-отчёт_${run.period_start}_${run.period_end}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css'] },
      })
      .from(container)
      .save();
  }, [report, run.period_start, run.period_end]);

  const isLoading = mutate.isPending || (!hasReport && waitingForGeneration);

  if (!llm?.multi_agent_available) return null;

  return (
    <div className="rounded-xl border border-indigo-200/80 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gradient-to-r from-indigo-50 via-purple-50/70 to-white border-b border-indigo-200/70">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white shrink-0">
            <Brain className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 text-sm leading-tight">
              AI-аналитик ресторана
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Badge
                variant="outline"
                className="text-[10px] font-normal text-indigo-700 border-indigo-200 bg-indigo-50 px-1.5 py-0 h-4"
              >
                4 агента
              </Badge>
              {report?.model && (
                <span className="text-[10px] text-slate-500 font-mono">
                  {report.model.replace(/-\d{8}$/, '')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasReport && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="text-indigo-700 hover:bg-indigo-100 h-8"
                onClick={handleDownloadPdf}
                title="Скачать отчёт (PDF)"
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-indigo-700 hover:bg-indigo-100 h-8"
                onClick={handleRegenerate}
                disabled={mutate.isPending}
                title="Перегенерировать отчёт"
              >
                {mutate.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
              </Button>
            </>
          )}
          {!hasReport && !isLoading && (
            <Button
              variant="default"
              size="sm"
              className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-sm h-8"
              onClick={handleGenerate}
              disabled={mutate.isPending}
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Сгенерировать отчёт
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      {isLoading && !hasReport ? (
        <div className="px-6 py-10 flex flex-col items-center justify-center gap-3 text-sm text-slate-600">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
          <div className="text-center">
            <div className="font-medium text-slate-900">
              Запускаем 4 агентов
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Sales · Schedule · Risks → Orchestrator
            </div>
            <div className="text-xs text-slate-400 mt-2">
              Обычно занимает 15–30 секунд
            </div>
          </div>
        </div>
      ) : !hasReport ? (
        <div className="px-6 py-8 text-sm text-slate-600 text-center">
          Отчёт ещё не сгенерирован. Нажми «Сгенерировать отчёт» — обычно занимает 15–30 секунд.
        </div>
      ) : (
        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="grid grid-cols-4 w-full rounded-none bg-slate-50/60 border-b border-slate-200 h-auto p-0">
            {TAB_DEFS.map((tab) => {
              const Icon = tab.icon;
              const filled =
                !!report &&
                !!report[tab.field] &&
                report[tab.field].trim().length > 0;
              return (
                <TabsTrigger
                  key={tab.key}
                  value={tab.key}
                  className="rounded-none flex items-center justify-center gap-1.5 text-xs sm:text-sm py-2.5 px-2 text-slate-600 border-b-2 border-transparent data-[state=active]:bg-white data-[state=active]:text-indigo-900 data-[state=active]:border-indigo-500 data-[state=active]:shadow-none font-medium transition-colors"
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {filled && (
                    <Check className="h-3 w-3 text-emerald-500 ml-0.5 hidden sm:inline" />
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {TAB_DEFS.map((tab) => {
            const content = report?.[tab.field] ?? '';
            return (
              <TabsContent
                key={tab.key}
                value={tab.key}
                className="mt-0 focus-visible:outline-none data-[state=inactive]:hidden"
              >
                <div className="max-h-[75vh] overflow-y-auto px-5 py-5 bg-white">
                  {content && content.trim().length > 0 ? (
                    <article
                      className={[
                        'prose prose-sm max-w-none',
                        // Headings
                        'prose-headings:font-semibold prose-headings:text-slate-900 prose-headings:scroll-mt-4',
                        'prose-h1:text-xl prose-h1:mb-3 prose-h1:mt-0 prose-h1:pb-2 prose-h1:border-b prose-h1:border-slate-200',
                        'prose-h2:text-base prose-h2:mt-5 prose-h2:mb-2 prose-h2:text-slate-800',
                        'prose-h3:text-sm prose-h3:mt-4 prose-h3:mb-1.5 prose-h3:text-slate-700 prose-h3:font-semibold',
                        // Body text
                        'prose-p:text-slate-700 prose-p:leading-relaxed prose-p:my-2',
                        'prose-strong:text-slate-900 prose-strong:font-semibold',
                        'prose-em:text-slate-700',
                        // Lists
                        'prose-ul:my-2 prose-ul:pl-5 prose-li:my-0.5 prose-li:text-slate-700 prose-li:marker:text-indigo-400',
                        'prose-ol:my-2 prose-ol:pl-5',
                        // Code & inline
                        'prose-code:text-indigo-700 prose-code:bg-indigo-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-none prose-code:after:content-none',
                        // Tables (GFM)
                        'prose-table:my-3 prose-table:text-xs prose-table:border prose-table:border-slate-200',
                        'prose-th:font-semibold prose-th:bg-slate-50 prose-th:text-slate-700 prose-th:p-2 prose-th:border prose-th:border-slate-200',
                        'prose-td:p-2 prose-td:border prose-td:border-slate-200 prose-td:text-slate-700',
                        // HR
                        'prose-hr:my-4 prose-hr:border-slate-200',
                        // Blockquote
                        'prose-blockquote:border-l-4 prose-blockquote:border-indigo-300 prose-blockquote:bg-indigo-50/50 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:not-italic prose-blockquote:text-slate-700',
                      ].join(' ')}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {content}
                      </ReactMarkdown>
                    </article>
                  ) : (
                    <div className="text-sm text-slate-500 italic text-center py-6">
                      {tab.placeholder}
                    </div>
                  )}
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      )}

      {mutate.error && (
        <div className="px-4 py-2 text-xs text-red-700 bg-red-50 border-t border-red-100">
          Ошибка генерации: {mutate.error.message}
        </div>
      )}

      {hasReport && report?.generated_at && (
        <div className="px-4 py-2 text-[10px] text-slate-500 border-t border-slate-200 flex items-center justify-between bg-slate-50/50">
          <span>
            Сгенерировано:{' '}
            {new Date(report.generated_at).toLocaleString('ru-RU')}
          </span>
          <span className="opacity-70 hidden sm:inline">
            Sales · Schedule · Risks → Orchestrator
          </span>
        </div>
      )}
    </div>
  );
};
